/**
 * @fileoverview The Weather page's own read of the forecast days and the issuing office (W24 Job D). It used to be the
 * app-wide weather context, read on every screen for the header; the header now reads its own small answer (GET /header,
 * WeatherContext), and only the Weather page reads this.
 *
 * The days are the server's (api/weather getForecastDays: each day's daytime and night, said in the reader's unit and
 * language with their sky keys); the app groups nothing. Kept under [...WEATHER_FORECAST_QUERY_KEY, zip, language]
 * (['weather', 'forecast', zip, 'es']): the garden's postcode (the owner's in a shared garden, useGardenPostcode), so a new
 * one asks afresh, and the language, so a switch reads the words again (B72). The unit is not in the key: a change of unit
 * reads it again here. Asked only when the garden place says Grow can plan from its postcode.
 *
 * How long it is kept is the server's: the header answer's keepMinutes (WeatherContext) is its stale time and its
 * interval; before the header answer is in, the app's default stale time and no interval.
 *
 * Where: features/weather WeatherPage.
 * API: GET /weather/forecast/user?lang=&unit= (api/weather).
 *
 * @module useWeatherDays
 */

import { useEffect, useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getForecastDays, type ForecastReply } from '@/shared/api/weather';
import { useGardenPostcode } from '@/shared/hooks/useGardenPostcode';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useWeatherContext } from '@/shared/hooks/useWeatherContext';
import { useUserConfig } from '@/shared/context/userConfigStore';
import { QUERY_STALE_TIME_MS } from '@/config/queryClient';

/**
 * The React Query key the Weather page's forecast days are cached under, followed by the postcode and the language. It
 * lived in config/tunables/ui while the weather context read the forecast; its one reader is here now (a value file is
 * not read by new code: scripts/check-value-files).
 * @kept K1
 */
export const WEATHER_FORECAST_QUERY_KEY: readonly string[] = ['weather', 'forecast'];

/** A minute, in ms: keepMinutes is in minutes. */
const MINUTE_MS = 60 * 1000;

/** What the Weather page is told: the server's days and office, and the read's state. */
export interface WeatherDaysReading {
  /** The answer, or undefined until it is in. */
  data: ForecastReply | undefined;
  /** True on the first read, with nothing held yet. */
  isLoading: boolean;
  /** The failure, or null. */
  error: Error | null;
}

/**
 * The Weather page's forecast days.
 *
 * @returns The server's days and office, with the read's state.
 */
export function useWeatherDays(): WeatherDaysReading {
  const { zip, usable } = useGardenPostcode();
  const { i18n: { language } } = useTranslation();
  const { userConfig } = useUserConfig();
  const { keepMinutes } = useWeatherContext();
  const queryClient = useQueryClient();
  const unit = userConfig.preferences.temperatureUnit === 'C' ? 'C' : 'F';
  /** The header answer's keep time in ms, or undefined before it is in. */
  const keepMs = typeof keepMinutes === 'number' ? keepMinutes * MINUTE_MS : undefined;
  const queryKey = [...WEATHER_FORECAST_QUERY_KEY, zip, language];

  const { data, isLoading, error } = useQuery({
    // The language is in the key: the days' words come in it (B72), so a switch reads them again.
    queryKey: [...WEATHER_FORECAST_QUERY_KEY, zip, language],
    queryFn: () => getForecastDays(language, unit),
    enabled: usable && zip.length > 0,
    staleTime: keepMs ?? QUERY_STALE_TIME_MS,
    refetchInterval: keepMs ?? false,
  });

  // The unit is not in the key (the page's address for the days is the postcode and language): a change reads them again.
  const unitSeen = useRef(unit);
  const keyText = JSON.stringify(queryKey);
  useEffect(() => {
    if (unitSeen.current === unit) return;
    unitSeen.current = unit;
    void queryClient.invalidateQueries({ queryKey: JSON.parse(keyText) as unknown[], exact: true });
  }, [unit, keyText, queryClient]);

  return { data, isLoading, error: (error as Error | null) ?? null };
}
